import React, { memo, useEffect, useRef } from 'react';
import { Chart } from '@dexma/ui-components';
import HeatmapData, {
    CategorizedMeta,
    DatetimeMeta,
    HeatmapAxis,
    HeatmapPalette,
    HeatmapSeries,
    HeatmapValue,
} from 'domain/entity/widgets/HeatmapData';
import { ColorType } from 'domain/entity/widgetData';
import Branding from 'domain/entity/branding';
import { ChartAxisType, Resolution } from 'domain/entity/widgets/ChartData';
import i18n from 'asset/translation';
import { defaultDateFormatter } from 'adapter/in/common/utils/widgetDataUtils';
import { AxisTypeValue, Options } from 'highcharts';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';
import { ChartRefObject, useChartExport } from 'adapter/in/advanced-analytics/context/ChartExport';
import { WIDGET_CHART, WidgetType } from 'domain/entity/widget';
import { roundTwoDecimals } from 'adapter/in/common/utils/numberFormatter';

export const HEATMAP_TURBO_THRESHOLD = 100000;
export const HEATMAP_COLOR_AXIS_PADDING_RATIO = 42;
export const HEATMAP_LEGEND_WIDTH_RATIO = 1.5;
export const HEATMAP_DAYS = [
    i18n.t('common.week.MONDAY'),
    i18n.t('common.week.TUESDAY'),
    i18n.t('common.week.WEDNESDAY'),
    i18n.t('common.week.THURSDAY'),
    i18n.t('common.week.FRIDAY'),
    i18n.t('common.week.SATURDAY'),
    i18n.t('common.week.SUNDAY'),
];

type HeatmapProps = {
    data: HeatmapData;
    height: number;
    width: number;
    isOnEdit: boolean;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
    dateFormat: string;
};

const Heatmap = (props: HeatmapProps) => {
    const { height, width, isOnEdit, data, decimalSeparator, thousandSeparator, branding, isLoading, dateFormat } = props;

    const chartComponentRef = useRef<ChartRefObject>(null);
    const { setChartRef } = useChartExport();

    useEffect(() => {
        if (setChartRef && chartComponentRef !== null) {
            setChartRef(chartComponentRef);
        }
    }, [setChartRef]);

    const chartContract = !isLoading
        ? {
              chart: {
                  zooming: {
                      type: isOnEdit || isLoading ? undefined : 'xy',
                  },
                  height: height - 4,
                  width: width - 4,
                  type: 'heatmap',
                  borderRadius: 8,
              },

              legend: {
                  symbolWidth: width / HEATMAP_LEGEND_WIDTH_RATIO,
              },
              xAxis: {
                  categories: mapAxisXCategories(data.type, data.axisX),
                  type: mapAxisType(data.axisX) as AxisTypeValue,
                  min: mapMin(data.axisX),
                  max: mapMax(data.axisX),
              },
              yAxis: {
                  min: mapMin(data.axisY),
                  max: mapMax(data.axisY),
                  reversed: true,
                  title: { text: null },
                  categories: mapCategories(data.axisY),
                  type: mapAxisType(data.axisY) as AxisTypeValue,
                  labels: {
                      format: mapLabelFormat(data.axisY),
                  },
                  tickPositions: mapTicks(data.axisY),
                  startOnTick: false,
                  endOnTick: false,
              },
              tooltip: {
                  headerFormat: `${data.series.title}<br/>`,
                  pointFormat: mapPointFormat(data.axisX, data.axisY, data.series.units),
              },
              colorAxis: {
                  // REVIEW Not part of Heatmap options type
                  stops: mapStops(data.series.palette, branding),
                  labels: {
                      style: {
                          fontSize: '10px',
                      },
                      width: width / HEATMAP_COLOR_AXIS_PADDING_RATIO,
                  },
              },
              title: {
                  text: data.description.title,
              },
              series: [
                  {
                      type: 'heatmap',
                      turboThreshold: HEATMAP_TURBO_THRESHOLD,
                      colsize: mapColsize(data.axisX, data.type),
                      rowsize: 1,
                      data: mapData(data.type, data.series, data.axisX, data.axisY, dateFormat),
                      states: {
                          hover: {
                              borderWidth: 2,
                              borderColor: branding.primaryColor,
                              brightness: 0,
                          },
                      },
                  },
              ],
          }
        : undefined;

    return (
        <Chart
            data-testid='heat-map'
            thousandsSep={thousandSeparator}
            decimalPoint={decimalSeparator}
            options={(chartContract as unknown) as Options}
        />
    );
};

const getMonthYearLabel = (label: string): string => {
    if (!label) return '';

    const [month, year] = label.split('-');
    const with3DigitsOrLess = month.length >= 3 ? month.slice(0, 3) : month;
    const normalized = with3DigitsOrLess.charAt(0).toUpperCase() + with3DigitsOrLess.slice(1).toLowerCase();

    return `${normalized} ${year}`;
};

const mapTicks = (axisY: HeatmapAxis): number[] | undefined => (axisY.type === ChartAxisType.DAY_HOURS ? [0, 4, 8, 12, 16, 20] : undefined);

const mapPointFormat = (axisX: HeatmapAxis, axisY: HeatmapAxis, units: string): string => {
    let xPart = '';
    let yPart = '';
    if (axisX.type === ChartAxisType.CATEGORIZED && axisY.type === ChartAxisType.CATEGORIZED) {
        return `{point.custom.tooltip}: <b>{point.value} ${units}</b>`;
    }
    if (axisX.type === ChartAxisType.DATETIME && axisY.type === ChartAxisType.WEEK_DAYS) {
        return `{point.custom.tooltip}: <b>{point.value} ${units}</b>`;
    }
    if (axisY.type === ChartAxisType.CATEGORIZED && (axisX.type === ChartAxisType.DATETIME || axisX.type === ChartAxisType.DATE)) {
        return `{point.custom.tooltip}: <b>{point.value} ${units}</b>`;
    }
    if (axisX.type === ChartAxisType.DATETIME) {
        xPart = `{point.x:%e %b, %Y}`;
    } else {
        xPart = `{point.x}`;
    }
    if (axisY.type === ChartAxisType.DAY_HOURS) {
        yPart = `{point.y}:00: <b>{point.value} ${units}</b>`;
    } else {
        yPart = `{point.y}: <b>{point.value} ${units}</b>`;
    }

    return `${xPart} ${yPart}`;
};

const mapColsize = (axis: HeatmapAxis, widgetType: WidgetType): number => {
    if (axis.type === ChartAxisType.DATETIME || axis.type === ChartAxisType.DATE) {
        switch ((axis.meta as DatetimeMeta).resolution) {
            case Resolution.W: {
                return 24 * 36e5 * 7;
            }
            case Resolution.D: {
                return 24 * 36e5;
            }
            case Resolution.HH: {
                return 36e5 / 2;
            }
            case Resolution.QH: {
                return 36e5 / 4;
            }
            case Resolution.TM: {
                return 36e5 / 6;
            }
            case Resolution.FM: {
                return 36e5 / 12;
            }
            default: {
                return widgetType === WIDGET_CHART.MULTI_DEVICE_HEATMAP ? 36e5 : 24 * 36e5;
            }
        }
    } else return 1;
};

const mapData = (type: WidgetType, series: HeatmapSeries, axisX: HeatmapAxis, axisY: HeatmapAxis, dateFormat: string) => {
    const normalizedValues = series.values.map((value: HeatmapValue) => ({
        ...value,
        v: parseFloat(roundTwoDecimals(value.v)),
    }));

    if (type === WidgetType.MULTI_DEVICE_HEATMAP) {
        return mapDataForMultiHeatmap(normalizedValues, axisX, axisY, dateFormat);
    }
    return mapDataForHeatmap(normalizedValues, axisX, axisY, dateFormat);
};

const mapDataForHeatmap = (values: HeatmapValue[], axisX: HeatmapAxis, axisY: HeatmapAxis, dateFormat: string) => {
    const addDays = (date: Date, numDays: number) => {
        const newDate = new Date(date);
        newDate.setDate(date.getDate() + numDays);
        return newDate;
    };

    if (axisX.type === ChartAxisType.CATEGORIZED && axisY.type === ChartAxisType.CATEGORIZED) {
        const categoriesX = (axisX.meta as CategorizedMeta).categories;
        const categoriesY = (axisY.meta as CategorizedMeta).categories;
        return values.length >= HEATMAP_TURBO_THRESHOLD
            ? values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v])
            : values.map((value: HeatmapValue) => ({
                  x: Number(value.x),
                  y: Number(value.y),
                  value: value.v,
                  custom: { tooltip: `${categoriesY[Number(value.y)]} ${categoriesX[Number(value.x)]}` },
              }));
    }
    if (axisX.type === ChartAxisType.DATETIME && axisY.type === ChartAxisType.WEEK_DAYS) {
        return values.length >= HEATMAP_TURBO_THRESHOLD
            ? values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v])
            : values.map((value: HeatmapValue) => ({
                  x: Number(value.x),
                  y: Number(value.y),
                  value: value.v,
                  custom: {
                      tooltip: `${defaultDateFormatter(addDays(value.x as Date, Number(value.y)), dateFormat.toUpperCase())} ${
                          HEATMAP_DAYS[Number(value.y)]
                      }`,
                  },
              }));
    }
    return axisX.type === ChartAxisType.DATETIME
        ? values.map((value: HeatmapValue) => [(value.x as Date).getTime(), Number(value.y), value.v])
        : values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v]);
};

const mapDataForMultiHeatmap = (values: HeatmapValue[], axisX: HeatmapAxis, axisY: HeatmapAxis, dateFormat: string) => {
    if (axisX.type === ChartAxisType.DATE && axisY.type === ChartAxisType.CATEGORIZED) {
        return values.map((value: HeatmapValue) => ({
            x: Number(new Date(value.x)),
            y: Number(value.y),
            value: value.v,
            custom: {
                tooltip: `${axisY.meta.categories[Number(value.y)]} - ${defaultDateFormatter(new Date(value.x), dateFormat.toUpperCase())}`,
            },
        }));
    }
    if (axisX.type === ChartAxisType.DATETIME && axisY.type === ChartAxisType.CATEGORIZED) {
        return values.map((value: HeatmapValue) => ({
            x: Number(value.x),
            y: Number(value.y),
            value: value.v,
            custom: {
                tooltip: `${axisY.meta.categories[Number(value.y)]} - ${defaultDateFormatter(
                    value.x as Date,
                    `${dateFormat.toUpperCase()} HH:mm`
                )}`,
            },
        }));
    }
    const categoriesX = (axisX.meta as CategorizedMeta).categories;
    const categoriesY = (axisY.meta as CategorizedMeta).categories;
    const getMonthYearLabelByIndex = (index: number): string => getMonthYearLabel(categoriesX[index]);

    return values.length >= HEATMAP_TURBO_THRESHOLD
        ? values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v])
        : values.map((value: HeatmapValue) => ({
              x: Number(value.x),
              y: Number(value.y),
              value: value.v,
              custom: { tooltip: `${categoriesY[Number(value.y)]} ${getMonthYearLabelByIndex(Number(value.x))}` },
          }));
};

const mapLabelFormat = (axis: HeatmapAxis): string | undefined => (axis.type === ChartAxisType.DAY_HOURS ? '{value}:00' : undefined);

const mapAxisType = (axis: HeatmapAxis): string => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return 'category';
        case ChartAxisType.DAY_HOURS:
            return 'linear';
        case ChartAxisType.DATE:
        case ChartAxisType.DATETIME:
        default:
            return 'datetime';
    }
};
const mapMin = (axis: HeatmapAxis): number | undefined => {
    // Highcharts has currently an issue, and if it has a 0 value on min for Heatmap
    // (with categorized axis and boost module) it explodes.
    if (axis.type === ChartAxisType.CATEGORIZED) return undefined;
    return axis.type !== ChartAxisType.DATETIME && axis.type !== ChartAxisType.DATE ? 0 : (axis.meta as DatetimeMeta).from.getTime();
};

const mapMax = (axis: HeatmapAxis): number | undefined => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return (axis.meta as CategorizedMeta).categories.length - 1;
        case ChartAxisType.DAY_HOURS:
            return 23;
        case ChartAxisType.DATETIME:
        default:
            return undefined;
    }
};
const mapCategories = (axis: HeatmapAxis): string[] | undefined => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return (axis.meta as CategorizedMeta).categories;
        case ChartAxisType.WEEK_DAYS:
            return HEATMAP_DAYS;
        default:
            return undefined;
    }
};

const mapAxisXCategories = (type: WidgetType, axis: HeatmapAxis): string[] | undefined => {
    if (type === WidgetType.MULTI_DEVICE_HEATMAP && axis.type === ChartAxisType.CATEGORIZED) {
        return (axis.meta as CategorizedMeta).categories.map((category: string) => getMonthYearLabel(category));
    }
    return mapCategories(axis);
};

const mapStops = (palette: HeatmapPalette, branding: Branding) => {
    if (palette.positions.length === palette.colors.length) {
        return palette.positions.map((position: number, index: number) => {
            switch (palette.colors[index].type) {
                case ColorType.PRIMARY: {
                    return [position, branding.primaryColor];
                }
                case ColorType.BACKGROUND: {
                    return [position, branding.backgroundColor];
                }
                case ColorType.BY_DEFAULT:
                default: {
                    return [position, palette.colors[index].byDefault];
                }
            }
        });
    }
    return [palette.positions[0], palette.colors[0].byDefault];
};

export default memo(Heatmap, checkMemoChanges);
